import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GET, POST, PUT, DELETE } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import {
  UserCheck,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  Eye,
  CheckCircle,
  AlertTriangle,
  Info,
  XCircle,
  Loader2,
  Phone,
  Mail,
  Building2,
  Briefcase,
  Download,
} from 'lucide-react';

const FILTER_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: '1_month', label: 'Last 1 Month' },
  { value: '3_months', label: 'Last 3 Months' },
  { value: '6_months', label: 'Last 6 Months' },
  { value: '12_months', label: 'Last 12 Months' },
  { value: 'all', label: 'All' },
];

const CATEGORY_OPTIONS = [
  'Manufacturing',
  'Trading & Export',
  'IT & Technology',
  'Healthcare & Medical',
  'Real Estate & Construction',
  'Finance & Insurance',
  'Education & Training',
  'Hospitality & Restaurant',
  'Retail & E-commerce',
  'Legal & Consultancy',
  'Marketing & Advertising',
  'Logistics & Transport',
  'Agriculture & Food',
  'Textile & Garments',
  'Automobile & Engineering',
  'Other',
];

const EMPTY_FORM = {
  id: null,
  name: '',
  companyName: '',
  bussinessCatagory: '',
  customBusinessCategory: '',
  email: '',
  phone: '',
  invitedBy: '',
  address: '',
  city_id: '',
  chapter_id: '',
};

const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
  `}</style>
);

const CustomToast = ({ message, severity, onClose }) => {
  if (!message) return null;
  const styles = {
    success: { bg: 'bg-emerald-600', icon: <CheckCircle className="w-6 h-6" /> },
    info: { bg: 'bg-blue-600', icon: <Info className="w-6 h-6" /> },
    warn: { bg: 'bg-amber-500', icon: <AlertTriangle className="w-6 h-6" /> },
    error: { bg: 'bg-red-500', icon: <XCircle className="w-6 h-6" /> },
  };
  const style = styles[severity] || styles.info;

  return (
    <div className={`fixed bottom-6 right-6 z-[100] px-6 py-3 rounded-xl shadow-2xl text-white flex items-center gap-3 ${style.bg}`}>
      {style.icon}
      <div className="flex-1">
        <div className="font-bold text-sm uppercase tracking-wide">{severity}</div>
        <div className="text-sm font-medium">{message}</div>
      </div>
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const Visitor = () => {
  const [visitors, setVisitors] = useState([]);
  const [cities, setCities] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dateFilter, setDateFilter] = useState('all');
  const master = useMasterChapter();
  const [filterCityId, setFilterCityId] = useState(master.cityId);
  const [filterChapterId, setFilterChapterId] = useState(master.chapterId);

  // Master chapter filter (super-admin header) locks the page's city/chapter filter
  useEffect(() => {
    setFilterCityId(master.cityId);
    setFilterChapterId(master.chapterId);
  }, [master.cityId, master.chapterId]);
  const [filterChapters, setFilterChapters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState('info');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const showToast = useCallback((message, severity = 'info') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const isEditing = useMemo(() => Boolean(formData.id), [formData.id]);

  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('filter', dateFilter);
      if (appliedSearch.trim()) params.set('search', appliedSearch.trim());
      if (filterCityId) params.set('city_id', filterCityId);
      if (filterChapterId) params.set('chapter_id', filterChapterId);

      const result = await GET(`/visitors?${params.toString()}`);
      setVisitors(result?.data || []);
    } catch (error) {
      showToast(error.message || 'Failed to fetch visitors', 'error');
    } finally {
      setLoading(false);
    }
  }, [dateFilter, appliedSearch, filterCityId, filterChapterId, showToast]);

  const fetchCities = useCallback(async () => {
    try {
      const data = await GET('/settings/cities');
      setCities(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Failed to fetch cities', error);
    }
  }, []);

  const fetchChapters = useCallback(async (cityId) => {
    if (!cityId) {
      setChapters([]);
      return;
    }
    try {
      const result = await GET(`/dashboard/chapters/city/${cityId}`);
      setChapters(result?.data || result || []);
    } catch (error) {
      console.error('Failed to fetch chapters', error);
      setChapters([]);
    }
  }, []);

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    if (formData.city_id) {
      fetchChapters(formData.city_id);
    } else {
      setChapters([]);
    }
  }, [formData.city_id, fetchChapters]);

  useEffect(() => {
    const loadFilterChapters = async () => {
      if (!filterCityId) {
        setFilterChapters([]);
        return;
      }
      try {
        const result = await GET(`/dashboard/chapters/city/${filterCityId}`);
        setFilterChapters(result?.data || result || []);
      } catch (error) {
        console.error('Failed to fetch filter chapters', error);
        setFilterChapters([]);
      }
    };
    loadFilterChapters();
  }, [filterCityId]);

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setChapters([]);
  };

  const openCreateModal = () => {
    resetForm();
    // New visitor: pre-fill the master chapter (still editable)
    if (master.chapterId) {
      setFormData({ ...EMPTY_FORM, city_id: master.cityId, chapter_id: master.chapterId });
      fetchChapters(master.cityId);
    }
    setShowFormModal(true);
  };

  const openEditModal = (visitor) => {
    const isKnownCategory = CATEGORY_OPTIONS.includes(visitor.bussinessCatagory);
    setFormData({
      id: visitor._id,
      name: visitor.name || '',
      companyName: visitor.companyName || '',
      bussinessCatagory: isKnownCategory ? visitor.bussinessCatagory : visitor.bussinessCatagory ? 'Other' : '',
      customBusinessCategory: isKnownCategory ? '' : visitor.bussinessCatagory || '',
      email: visitor.email || '',
      phone: visitor.phone || '',
      invitedBy: visitor.invitedBy || '',
      address: visitor.address || '',
      city_id: visitor.city_id?._id || visitor.city_id || '',
      chapter_id: visitor.chapter_id?._id || visitor.chapter_id || '',
    });
    setShowFormModal(true);
  };

  const openViewModal = (visitor) => {
    setSelectedVisitor(visitor);
    setShowViewModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'city_id') {
      setFormData((prev) => ({ ...prev, city_id: value, chapter_id: '' }));
      return;
    }

    if (name === 'bussinessCatagory') {
      setFormData((prev) => ({
        ...prev,
        bussinessCatagory: value,
        customBusinessCategory: value === 'Other' ? prev.customBusinessCategory : '',
      }));
      return;
    }

    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: value.replace(/\D/g, '').slice(0, 10) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const category =
      formData.bussinessCatagory === 'Other'
        ? formData.customBusinessCategory.trim()
        : formData.bussinessCatagory;

    return {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      companyName: formData.companyName.trim(),
      bussinessCatagory: category,
      invitedBy: formData.invitedBy.trim(),
      address: formData.address.trim(),
      ...(formData.city_id && { city_id: formData.city_id }),
      ...(formData.chapter_id && { chapter_id: formData.chapter_id }),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim()) {
      showToast('Name and phone are required', 'warn');
      return;
    }

    if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
      showToast('Phone number must be 10 digits', 'warn');
      return;
    }

    if (formData.bussinessCatagory === 'Other' && !formData.customBusinessCategory.trim()) {
      showToast('Please specify business category', 'warn');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();

      if (isEditing) {
        await PUT(`/visitors/${formData.id}`, payload);
        showToast('Visitor updated successfully', 'success');
      } else {
        await POST('/visitors', payload);
        showToast('Visitor created successfully', 'success');
      }

      setShowFormModal(false);
      resetForm();
      fetchVisitors();
    } catch (error) {
      showToast(error.message || 'Failed to save visitor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await DELETE(`/visitors/${deleteTarget._id}`);
      showToast('Visitor deleted successfully', 'success');
      setDeleteTarget(null);
      fetchVisitors();
    } catch (error) {
      showToast(error.message || 'Failed to delete visitor', 'error');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams();
      params.set('filter', dateFilter);
      if (appliedSearch.trim()) params.set('search', appliedSearch.trim());
      if (filterCityId) params.set('city_id', filterCityId);
      if (filterChapterId) params.set('chapter_id', filterChapterId);

      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/visitors/download-pdf?${params.toString()}`,
        {
          method: 'GET',
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `visitors_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('PDF downloaded successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to download PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 text-sm';

  return (
    <div>
      <CustomStyles />
      <CustomToast message={toastMessage} severity={toastSeverity} onClose={() => setToastMessage(null)} />

      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-blue-600" />
              Visitors
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage visitor registrations from landing page</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-5 py-3 rounded-xl font-bold shadow-lg shadow-emerald-200 transition-colors"
            >
              {downloadingPdf ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </button>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-bold shadow-lg shadow-blue-200 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Visitor
            </button>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Date Filter
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 text-sm font-semibold text-slate-700 bg-white"
              >
                {FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                City
              </label>
              <select
                value={filterCityId}
                onChange={(e) => {
                  setFilterCityId(e.target.value);
                  setFilterChapterId('');
                }}
                disabled={!!master.chapterId}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 text-sm font-semibold text-slate-700 bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">All Cities</option>
                {cities.map((city) => (
                  <option key={city._id} value={city._id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Chapter
              </label>
              <select
                value={filterChapterId}
                onChange={(e) => setFilterChapterId(e.target.value)}
                disabled={!filterCityId || !!master.chapterId}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 text-sm font-semibold text-slate-700 bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">{filterCityId ? 'All Chapters' : 'Select city first'}</option>
                {filterChapters.map((chapter) => (
                  <option key={chapter._id} value={chapter._id}>
                    {chapter.name}
                  </option>
                ))}
              </select>
            </div>

            <form onSubmit={handleSearchSubmit} className="flex gap-2 items-end">
              <div className="relative flex-1">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Search
                </label>
                <Search className="w-4 h-4 text-slate-400 absolute left-3 bottom-3" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name or phone"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 text-sm"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors"
              >
                Search
              </button>
            </form>
          </div>
          {master.chapterId && <div className="mb-4"><MasterChapterLockNote /></div>}

          <div className="overflow-x-auto rounded-2xl border border-slate-200 custom-scrollbar">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {['Name', 'Phone', 'Email', 'Company', 'Category', 'Invited By', 'Date', 'Actions'].map((head) => (
                    <th
                      key={head}
                      className="px-4 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
                      Loading visitors...
                    </td>
                  </tr>
                ) : visitors.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-400 font-medium">
                      No visitors found.
                    </td>
                  </tr>
                ) : (
                  visitors.map((visitor) => (
                    <tr key={visitor._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-4 text-sm font-semibold text-slate-800 whitespace-nowrap">
                        {visitor.name || '-'}
                        {visitor.source === 'app' && (
                          <span className="ml-2 inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full border bg-violet-50 text-violet-700 border-violet-100">From App</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">{visitor.phone || '-'}</td>
                      <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">{visitor.email || '-'}</td>
                      <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">{visitor.companyName || '-'}</td>
                      <td className="px-4 py-4 text-sm text-slate-600 max-w-[180px] truncate">{visitor.bussinessCatagory || '-'}</td>
                      <td className="px-4 py-4 text-sm text-slate-600 max-w-[160px] truncate">{visitor.invitedBy || '-'}</td>
                      <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(visitor.createdAt)}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openViewModal(visitor)}
                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* App signups are user accounts, not visitor records — view only */}
                          {visitor.source !== 'app' && (
                            <>
                              <button
                                onClick={() => openEditModal(visitor)}
                                className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(visitor)}
                                className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-slate-800">
                {isEditing ? 'Edit Visitor' : 'Add Visitor'}
              </h3>
              <button onClick={() => setShowFormModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Full Name *</label>
                  <input name="name" value={formData.name} onChange={handleFormChange} className={inputClass} placeholder="Visitor name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Phone *</label>
                  <input name="phone" value={formData.phone} onChange={handleFormChange} maxLength={10} inputMode="numeric" className={inputClass} placeholder="10 digit phone" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleFormChange} className={inputClass} placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Company Name</label>
                  <input name="companyName" value={formData.companyName} onChange={handleFormChange} className={inputClass} placeholder="Company name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Business Category</label>
                  <select name="bussinessCatagory" value={formData.bussinessCatagory} onChange={handleFormChange} className={inputClass}>
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {formData.bussinessCatagory === 'Other' && (
                    <input
                      name="customBusinessCategory"
                      value={formData.customBusinessCategory}
                      onChange={handleFormChange}
                      className={`${inputClass} mt-3`}
                      placeholder="Enter category"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Invited By</label>
                  <input name="invitedBy" value={formData.invitedBy} onChange={handleFormChange} className={inputClass} placeholder="Invited by" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">City</label>
                  <select name="city_id" value={formData.city_id} onChange={handleFormChange} className={inputClass}>
                    <option value="">Select city</option>
                    {cities.map((city) => (
                      <option key={city._id} value={city._id}>{city.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-2">Chapter</label>
                  <select name="chapter_id" value={formData.chapter_id} onChange={handleFormChange} className={inputClass} disabled={!formData.city_id}>
                    <option value="">{formData.city_id ? 'Select chapter' : 'Select city first'}</option>
                    {chapters.map((chapter) => (
                      <option key={chapter._id} value={chapter._id}>{chapter.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-600 mb-2">Address</label>
                <textarea name="address" value={formData.address} onChange={handleFormChange} rows="3" className={inputClass} placeholder="Address" />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowFormModal(false)} className="px-6 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isEditing ? 'Update Visitor' : 'Create Visitor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selectedVisitor && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Visitor Details
              </h3>
              <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Name', value: selectedVisitor.name, icon: UserCheck },
                { label: 'Phone', value: selectedVisitor.phone, icon: Phone },
                { label: 'Email', value: selectedVisitor.email || '-', icon: Mail },
                { label: 'Company', value: selectedVisitor.companyName || '-', icon: Building2 },
                { label: 'Category', value: selectedVisitor.bussinessCatagory || '-', icon: Briefcase },
                { label: 'Invited By', value: selectedVisitor.invitedBy || '-' },
                { label: 'Source', value: selectedVisitor.source === 'app' ? 'From App (signed up as user)' : 'Added by admin' },
                { label: 'City', value: selectedVisitor.city_id?.name || '-' },
                { label: 'Chapter', value: selectedVisitor.chapter_id?.name || '-' },
                { label: 'Registered On', value: formatDate(selectedVisitor.createdAt) },
              ].map((item) => (
                <div key={item.label} className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-1">{item.label}</p>
                  <p className="text-sm font-semibold text-slate-800 break-words">{item.value}</p>
                </div>
              ))}
              <div className="md:col-span-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase mb-1">Address</p>
                <p className="text-sm font-semibold text-slate-800 break-words">{selectedVisitor.address || '-'}</p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end">
              <button onClick={() => setShowViewModal(false)} className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[110] p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Visitor</h3>
            <p className="text-slate-500 mb-6 font-medium">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold">
                Cancel
              </button>
              <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visitor;
